import type {
    CardAttributeEntityObject,
    CardAttributeQueryParams,
} from "src/app/features/CardImplementation/subFeatures/CardDefinition/subFeatures/CardAttribute/services/server/CardAttributesQueryTypes";
import { getAssignedDataGroup } from "../../../../../../../../../core/router/auth";
import { ResourceName } from "../../../../../../../../../core/router/config/ResourceName";
import { FORM_ACTION } from "../../../../../../../../../core/router/types/routerTypes";
import { baseAuthApi } from "../../../../../../../../../core/service/baseApi";
import type { FormMutation } from "../../../../../../../../../core/service/types/FormMutation";
import type { Dto } from "../../../../../../../../../core/types/be/Dto";
import type { CardAttributeCopyEntity } from "../../types/CardAttributeCopyEntity";
import type { CardAttributeEntity } from "../../types/CardAttributeEntity";
import { CardAttributeCopyEntityToCopyDto } from "../mappers/CardAttributeCopyEntityToCopyDto";
import { CardAttributeDtoToEntity } from "../mappers/CardAttributeDtoToEntity";
import { CardAttributeEntityToDto } from "../mappers/CardAttributeEntityToDto";

const CardAttributeApi = baseAuthApi.injectEndpoints({
    endpoints: (builder) => ({
        CardAttributeLoad: builder.query<CardAttributeEntity, CardAttributeQueryParams>({
            providesTags: [ResourceName.CARD_ATTRIBUTES],
            query: (cardAttributeQueryParams) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.CARD_IMPLEMENTATIONS}/${cardAttributeQueryParams.cardImplementationId}/${ResourceName.CARD_DEFINITIONS}/${cardAttributeQueryParams.cardDefinitionId}/${ResourceName.CARD_ATTRIBUTES}/${cardAttributeQueryParams.id}`,
                    method: "GET",
                };
            },
            transformResponse: (response: Dto, _) => {
                return CardAttributeDtoToEntity(response);
            },
        }),
        CardAttributeSave: builder.mutation<CardAttributeEntity, CardAttributeEntityObject>({
            query: ({ cardAttributeFormData, cardAttributeQuery }) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.CARD_IMPLEMENTATIONS}/${cardAttributeQuery.cardImplementationId}/${ResourceName.CARD_DEFINITIONS}/${cardAttributeQuery.cardDefinitionId}/${ResourceName.CARD_ATTRIBUTES}`;
                const method = "POST";

                const body = CardAttributeEntityToDto(cardAttributeFormData);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.CARD_ATTRIBUTES],
        }),
        CardAttributeUpdate: builder.mutation<CardAttributeEntity, CardAttributeEntityObject>({
            query: ({ cardAttributeFormData, cardAttributeQuery }) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.CARD_IMPLEMENTATIONS}/${cardAttributeQuery.cardImplementationId}/${ResourceName.CARD_DEFINITIONS}/${cardAttributeQuery.cardDefinitionId}/${ResourceName.CARD_ATTRIBUTES}/${cardAttributeQuery.id}`;
                const method = "PUT";

                const body = CardAttributeEntityToDto(cardAttributeFormData);

                return {
                    url,
                    method,
                    body,
                };
            },
            invalidatesTags: [ResourceName.CARD_ATTRIBUTES],
        }),
        CardAttributeCopy: builder.mutation<
            CardAttributeEntity,
            FormMutation<CardAttributeCopyEntity> & { cardImplementationId: string; cardDefinitionId: string }
        >({
            query: ({ data, cardImplementationId, cardDefinitionId }) => {
                const dataGroup = getAssignedDataGroup();

                const url = `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.CARD_IMPLEMENTATIONS}/${cardImplementationId}/${ResourceName.CARD_DEFINITIONS}/${cardDefinitionId}/${ResourceName.CARD_ATTRIBUTES}/${data.id}/${FORM_ACTION.COPY}`;

                const method = "POST";
                const body = CardAttributeCopyEntityToCopyDto(data);

                return { url, method, body };
            },
            invalidatesTags: [ResourceName.CARD_ATTRIBUTES],
        }),
        CardAttributeDelete: builder.mutation<CardAttributeEntity, CardAttributeQueryParams>({
            query: (cardAttributeQueryParams) => {
                const dataGroup = getAssignedDataGroup();
                return {
                    url: `${ResourceName.DATA_GROUPS}/${dataGroup?.code}/${ResourceName.CARD_IMPLEMENTATIONS}/${cardAttributeQueryParams.cardImplementationId}/${ResourceName.CARD_DEFINITIONS}/${cardAttributeQueryParams.cardDefinitionId}/${ResourceName.CARD_ATTRIBUTES}/${cardAttributeQueryParams.id}`,
                    method: "DELETE",
                };
            },
            invalidatesTags: [ResourceName.CARD_ATTRIBUTES],
        }),
    }),
});

export const {
    useCardAttributeLoadQuery,
    useCardAttributeSaveMutation,
    useCardAttributeUpdateMutation,
    useCardAttributeCopyMutation,
    useCardAttributeDeleteMutation,
} = CardAttributeApi;
